import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { contentFlowStartY, layoutButtonRows, type ButtonSlot } from "@/ui/buttonLayout";
import { createNameEntryModal, type NameEntryModalHandle } from "@/ui/NameEntryModal";
import { DailyChallengeClient } from "@/daily/DailyChallengeClient";
import { DAILY_SCORING_RULES_VERSION } from "@/daily/types";
import { DailyLocalState } from "@/daily/DailyLocalState";
import { buildQaPreviewContext, shouldSubmitDailyResult } from "@/daily/qaPreview";
import { DAILY_INCOMPLETE_LABEL } from "@/daily/statusLabels";
import { ScoreChallengeClient } from "@/daily/ScoreChallengeClient";
import { createShareChallengeModal, type ShareChallengeModalHandle } from "@/ui/ShareChallengeModal";
import { resolveChallengeBaseUrl, buildChallengeUrl, isSharingEnabled } from "@/daily/shareUrl";
import { ScoreChallengeSession } from "@/daily/scoreChallengeSession";
import { compareToTarget, describeComparison, marginBucket } from "@/daily/scoreChallenge";
import {
  trackScoreChallengeReshared,
  trackScoreChallengeVerifiedResult,
  trackScoreSharePromptViewed,
  trackWorldsDiscoveryClicked,
  trackDailySubmissionFailed,
  trackPlayAgainClicked,
} from "@/analytics/gameAnalytics";
import type { ScoreComparisonResult } from "@/daily/scoreChallenge";
import type { ShareContext, ResultSource } from "@/analytics/events";
import type { DailyResultSceneData } from "@/daily/DailyRunContext";
import type { Platform } from "@/platform/Platform";
import { detectPlatform } from "@/platform/detectPlatform";
import { OnboardingStorage } from "@/progress/OnboardingStorage";
import { resolveQaWorldsDiscoveryOverride } from "@/qa/onboardingQa";
import { attachOrientationGate, type OrientationGateHandle } from "@/scenes/OrientationGate";

type SubmitState = "submitting" | "done" | "offline" | "error";

/**
 * The post-attempt screen: completed/DNF, score, active play time, steps,
 * current daily best, current rank (if available), attempts remaining,
 * TRY AGAIN (if attempts remain)/DAILY LEADERBOARD/MAIN MENU. Owns the
 * actual network submission (`DailyChallengeClient.submitResult()`) - by
 * the time this scene is reached, the attempt itself is already spent
 * (reservation happened before `GameScene` ever started); this screen's
 * job is only to report the outcome and refresh the authoritative state.
 */
export class DailyResultScene extends Phaser.Scene {
  private resultData!: DailyResultSceneData;
  /** PR34: this attempt's frozen platform (carried from `GameScene`) - the Daily board its best/rank/challenge source belong to. */
  private platform: Platform = "desktop";
  private submitState: SubmitState = "submitting";
  private rank: number | null = null;
  private hasRank = false;
  private nameModal!: NameEntryModalHandle;
  private mainContainer!: Phaser.GameObjects.Container;
  /** Set once `submit()` resolves, only when `resultData.sourceChallenge` is present and the server-verified result carries a real Daily Points value - the win/tie/loss comparison against that challenge's frozen target. `null` for every normal (non-challenge) attempt. */
  private comparison: ScoreComparisonResult | null = null;
  private isCreatingChallenge = false;
  private shareCreateError = "";
  private shareModal!: ShareChallengeModalHandle;
  /**
   * Instant Play & Onboarding V1: decided ONCE in `create()` - identical
   * shape to `EndlessResultScene`'s own field, sharing the SAME global
   * `OnboardingStorage` flag, so the promotion is shown at most once
   * across Endless/Daily/Beat My Score alike, whichever result screen a
   * player reaches first. Never shown for a QA preview.
   */
  private shouldShowWorldsDiscovery = false;
  private worldsDiscoveryResultSource: ResultSource = "daily";
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("DailyResultScene");
  }

  create(data: DailyResultSceneData): void {
    this.platform = data.platform ?? detectPlatform();
    this.resultData = data;
    this.submitState = "submitting";
    this.rank = null;
    this.hasRank = false;
    this.comparison = null;
    this.isCreatingChallenge = false;
    this.shareCreateError = "";
    this.worldsDiscoveryResultSource = data.sourceChallenge !== undefined ? "beat_my_score" : "daily";

    const discoveryOverride = resolveQaWorldsDiscoveryOverride(window.location.hostname, window.location.search);
    if (data.isQaPreview) {
      this.shouldShowWorldsDiscovery = false;
    } else if (discoveryOverride) {
      this.shouldShowWorldsDiscovery = discoveryOverride === "pending";
    } else {
      this.shouldShowWorldsDiscovery = !OnboardingStorage.hasShownWorldsDiscovery();
      if (this.shouldShowWorldsDiscovery) OnboardingStorage.setWorldsDiscoveryShown();
    }

    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.mainContainer = this.add.container(0, 0);
    this.nameModal = createNameEntryModal(this, 90, {
      onSave: (name) => this.handleSaveName(name),
      onNotNow: () => {},
    });
    const shareContext: ShareContext = data.sourceChallenge !== undefined ? "reshare" : "own_result";
    this.shareModal = createShareChallengeModal(this, 95, shareContext);

    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();

    // A QA preview never reserved a real attempt (see
    // `DailyChallengeScene.startQaPreview()`), so there is nothing here
    // for `daily-result` to legitimately attach a result to - never call
    // it, never fetch/overwrite real status or rank, never open the
    // real-result name prompt.
    if (shouldSubmitDailyResult(data.isQaPreview)) {
      void this.submit();
    }
  }

  private teardown(): void {
    this.scale.off("resize", this.onResize, this);
    this.nameModal.destroy();
    this.shareModal.destroy();
  }

  private onResize(): void {
    this.buildLayout();
    this.nameModal.relayout();
    this.shareModal.relayout();
  }

  private async submit(): Promise<void> {
    const { spec, attemptNumber, outcome } = this.resultData;
    const result = await DailyChallengeClient.submitResult({
      challengeId: spec.challengeId,
      attemptNumber,
      status: outcome.status,
      score: outcome.score,
      finalLength: outcome.finalLength,
      activeDurationMs: outcome.activeDurationMs,
      steps: outcome.steps,
      scoringRulesVersion: DAILY_SCORING_RULES_VERSION,
      platform: this.platform,
    });

    if (result.state !== "ok") {
      this.submitState = result.state === "offline" ? "offline" : "error";
      // PR28: Daily's own equivalent of `trackEndlessSubmissionFailed()` -
      // fire-and-forget, purely additive telemetry. Must never affect the
      // RETRY SAVE flow below: `trackDailySubmissionFailed()` routes
      // through `capture()`, which already never throws (see
      // `posthogClient.ts`), so this can't interfere with `submitState`/
      // `buildLayout()` either way.
      trackDailySubmissionFailed({
        challengeId: spec.challengeId,
        generatorVersion: spec.generatorVersion,
        attemptNumber,
        failureCategory: this.submitState === "offline" ? "offline" : "error",
        retryable: true,
      });
      this.buildLayout();
      return;
    }

    const [statusResult, rankResult, nameResult] = await Promise.all([
      DailyChallengeClient.getMyStatus(spec.challengeId, this.platform),
      DailyChallengeClient.getMyRank(spec.challengeId, this.platform),
      DailyChallengeClient.getMyDisplayName(),
    ]);

    if (statusResult.state === "ok" && statusResult.status) {
      DailyLocalState.save({
        challengeId: spec.challengeId,
        attemptsRemaining: Math.max(0, 3 - statusResult.status.attemptsReserved),
        bestScore: statusResult.status.bestScore,
        bestCompleted: statusResult.status.bestCompleted,
      });
    }
    if (rankResult.state === "ok" && rankResult.rank?.hasResult) {
      this.rank = rankResult.rank.rank;
      this.hasRank = true;
    }

    // "Beat My Score" comparison (PR10): only for a challenge-sourced
    // attempt whose server-verified result actually carries a Daily
    // Points value (always true for a "completed" V2 outcome - see
    // `outcome.dailyPoints`'s doc - but never assumed). Computed from
    // THIS scene's own already-known `outcome.dailyPoints` (the value
    // `daily-result.ts` just independently recomputed and stored, echoed
    // back to the client that submitted it - never re-fetched, and never
    // the client's own pre-submission guess) against the frozen
    // `sourceChallenge.targetDailyPoints` - never re-fetches the
    // challenge itself, so an already-improved OR already-expired
    // original challenge can never change what this screen shows for
    // this attempt.
    const sourceChallenge = this.resultData.sourceChallenge;
    if (sourceChallenge && outcome.dailyPoints !== null) {
      this.comparison = compareToTarget(outcome.dailyPoints, sourceChallenge.targetDailyPoints);
      if (!ScoreChallengeSession.hasEventFired("score_challenge_verified_result", sourceChallenge.publicId)) {
        ScoreChallengeSession.markEventFired("score_challenge_verified_result", sourceChallenge.publicId);
        trackScoreChallengeVerifiedResult(this.comparison.outcome, marginBucket(this.comparison.margin), spec.generatorVersion);
      }
    }

    this.submitState = "done";
    this.buildLayout();

    // "If no display name exists, request one before publishing the
    // first daily result" - the result is already published by this
    // point (submission is best-effort/never blocked on a name), this is
    // simply the first moment a name can meaningfully be requested.
    if (nameResult.state === "ok" && nameResult.data === null) {
      this.nameModal.open({ headline: "SAVE YOUR NAME", contextLine: "Show your name on the Daily Challenge leaderboard." });
    }
  }

  private async handleSaveName(name: string): Promise<{ ok: boolean; error?: string }> {
    const result = await DailyChallengeClient.setDisplayName(name);
    if (result.state !== "ok") {
      return { ok: false, error: result.state === "error" ? result.message : "Could not save name right now." };
    }
    return { ok: true };
  }

  /**
   * CHALLENGE A FRIEND: always creates/retrieves a link for the CALLER's
   * OWN current verified best (`ScoreChallengeClient.createOrGetChallenge()`
   * independently re-derives everything server-side) - never the original
   * creator's target, even when this attempt itself came from a challenge
   * link (`resultData.sourceChallenge`). That's what makes "re-share after
   * a comparison" and "share my own fresh result" the exact same code
   * path; only the `context` passed to analytics (and the extra
   * `score_challenge_reshared` marker) differs.
   *
   * This only ever CREATES/RETRIEVES the link and opens the in-game
   * disclosure modal (round-5 fix) - it never itself invokes the OS share
   * sheet or the clipboard; those only happen from an explicit SHARE/COPY
   * LINK tap inside `ShareChallengeModal`. `score_share_prompt_viewed`
   * fires the moment that modal opens - see the modal's own doc for why
   * that replaced the previous "button becomes visible" firing point.
   */
  private async challengeAFriend(): Promise<void> {
    if (this.isCreatingChallenge || !isSharingEnabled()) return;
    this.isCreatingChallenge = true;
    this.shareCreateError = "";
    this.buildLayout();

    const isReshare = this.resultData.sourceChallenge !== undefined;

    const result = await ScoreChallengeClient.createOrGetChallenge(this.platform);
    this.isCreatingChallenge = false;

    if (result.state !== "ok" || !result.publicId || result.targetDailyPoints === undefined) {
      this.shareCreateError = "Could not create your challenge link. Try again.";
      this.buildLayout();
      return;
    }

    if (isReshare && this.comparison) {
      trackScoreChallengeReshared(this.comparison.outcome);
    }

    this.buildLayout();
    const baseUrl = resolveChallengeBaseUrl(window.location.hostname, window.location.origin);
    const url = buildChallengeUrl(baseUrl, result.publicId);
    trackScoreSharePromptViewed(isReshare ? "reshare" : "own_result");
    this.shareModal.open(result.creatorDisplayName ?? "ANONYMOUS", result.targetDailyPoints, url);
  }

  private tryAgain(): void {
    trackPlayAgainClicked({ gameMode: "daily" });
    this.scene.start("DailyChallengeScene");
  }

  /** Instant Play & Onboarding V1: opens the existing `WorldSelectScene` exactly as Main Menu's WORLDS button does - never auto-navigates further, never replaces the Daily/Beat My Score result or its comparison banner above it. */
  private exploreTheWorlds(): void {
    trackWorldsDiscoveryClicked(this.worldsDiscoveryResultSource);
    this.scene.start("WorldSelectScene");
  }

  /** REPLAY QA PREVIEW - another unlimited, unranked run of the same spec, never a real reservation. */
  private replayQaPreview(): void {
    this.scene.start("GameScene", { daily: buildQaPreviewContext(this.resultData.spec) });
  }

  private buildLayout(): void {
    this.mainContainer.removeAll(true);
    const { width, height } = this.scale;
    const centerX = width / 2;
    const { spec, outcome, attemptNumber, attemptsRemaining, isQaPreview, sourceChallenge } = this.resultData;

    let y = height * 0.1;

    const title = this.add
      .text(centerX, y, outcome.status === "completed" ? "ATTEMPT COMPLETE" : DAILY_INCOMPLETE_LABEL, {
        fontFamily: "Arial, sans-serif",
        fontSize: "30px",
        fontStyle: "bold",
        color: outcome.status === "completed" ? "#7dffb3" : "#ff5f6d",
      })
      .setOrigin(0.5);
    this.mainContainer.add(title);
    y += 46;

    if (isQaPreview) {
      const qaBadge = this.add
        .text(centerX, y, "QA PREVIEW — NOT RANKED", {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          fontStyle: "bold",
          color: "#0f1117",
          backgroundColor: "#ffd166",
          padding: { x: 8, y: 4 },
        })
        .setOrigin(0.5);
      this.mainContainer.add(qaBadge);
      y += 34;
    } else {
      const attemptLabel = this.add
        .text(centerX, y, `DAILY · ATTEMPT ${attemptNumber}/3`, {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          color: "#8b93a7",
        })
        .setOrigin(0.5);
      this.mainContainer.add(attemptLabel);
      y += 30;
    }

    // Daily Points is the prominent, headline number for a V2 (Generator
    // V2 / Daily Survival) result - shown above the raw SCORE line, never
    // in place of it (the raw metrics it's built from stay fully visible
    // below). A V1 (legacy) date has no scoring profile at all, so
    // `outcome.dailyPoints` is null and this line is skipped entirely -
    // never a fabricated number for a format that doesn't compute one.
    if (outcome.dailyPoints !== null) {
      const pointsLine = this.add
        .text(centerX, y, `DAILY POINTS: ${outcome.dailyPoints}`, {
          fontFamily: "Arial, sans-serif",
          fontSize: "26px",
          fontStyle: "bold",
          color: "#ffd166",
        })
        .setOrigin(0.5);
      this.mainContainer.add(pointsLine);
      y += 36;
    }

    const scoreLine = this.add
      .text(centerX, y, `SCORE: ${outcome.score}`, { fontFamily: "Arial, sans-serif", fontSize: "22px", fontStyle: "bold", color: "#f5f7fa" })
      .setOrigin(0.5);
    this.mainContainer.add(scoreLine);
    y += 30;

    const breakdown = this.add
      .text(centerX, y, `BITE ${outcome.biteScore} · SKILL ${outcome.skillScore} · PACE ${outcome.paceBonus}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);
    this.mainContainer.add(breakdown);
    y += 24;

    const timeSeconds = Math.floor(outcome.activeDurationMs / 1000);
    const timeLabel = `${Math.floor(timeSeconds / 60)}:${String(timeSeconds % 60).padStart(2, "0")}`;
    const timeSteps = this.add
      .text(centerX, y, `LENGTH ${outcome.finalLength} · TIME ${timeLabel} · STEPS ${outcome.steps}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);
    this.mainContainer.add(timeSteps);
    y += 32;

    if (isQaPreview) {
      this.renderQaPreviewActions(centerX, y, width, height);
      return;
    }

    if (this.submitState === "submitting") {
      const saving = this.add
        .text(centerX, y, "Saving your result…", { fontFamily: "Arial, sans-serif", fontSize: "14px", color: "#8b93a7" })
        .setOrigin(0.5);
      this.mainContainer.add(saving);
      y += 30;
    } else if (this.submitState === "offline" || this.submitState === "error") {
      const failMsg = this.add
        .text(centerX, y, "Couldn't save your result. Check your connection and retry.", {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          color: "#ff5f6d",
          align: "center",
          wordWrap: { width: Math.min(420, width - 40) },
        })
        // Top-anchored (not center-anchored) - same reasoning as
        // `renderQaPreviewActions()`'s `note`/the share-error `feedback`
        // text below: this can wrap to multiple lines, and the very next
        // line computes its own y from `failMsg.height`, which only stays
        // correct (grows downward only, never encroaching on `timeSteps`
        // above it) if the text's own top edge - not its center - sits at
        // the `y` it was given.
        .setOrigin(0.5, 0);
      this.mainContainer.add(failMsg);
      y += failMsg.height + 12;
      const retry = createButton(this, centerX, y, "RETRY SAVE", () => void this.submit(), { width: 200, height: 44 });
      this.mainContainer.add(retry);
      y += 60;
    } else {
      if (this.hasRank && this.rank !== null) {
        const rankLine = this.add
          .text(centerX, y, `CURRENT RANK: #${this.rank}`, { fontFamily: "Arial, sans-serif", fontSize: "15px", fontStyle: "bold", color: "#5b8cff" })
          .setOrigin(0.5);
        this.mainContainer.add(rankLine);
        y += 28;
      }
    }

    const attemptsLine = this.add
      .text(centerX, y, `Attempts remaining: ${attemptsRemaining}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: attemptsRemaining > 0 ? "#7dffb3" : "#ff5f6d",
      })
      .setOrigin(0.5);
    this.mainContainer.add(attemptsLine);
    y += 36;

    if (attemptsRemaining <= 0) {
      const doneLine = this.add
        .text(centerX, y, "That was your last attempt today. Come back after the reset!", {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          color: "#8b93a7",
          align: "center",
          wordWrap: { width: Math.min(420, width - 40) },
        })
        .setOrigin(0.5);
      this.mainContainer.add(doneLine);
      y += doneLine.height + 20;
    }

    // "Beat My Score" (PR10): the win/tie/loss comparison (only for a
    // challenge-sourced attempt whose server-verified result carries a
    // Daily Points value - see `submit()`) renders above CHALLENGE A
    // FRIEND, which is itself only ever offered for a ranked, verified,
    // completed Daily Points result - never for QA, never for
    // "incomplete", never while still submitting/offline/erroring
    // (`canShare` below mirrors the same "already accepted by the
    // server" gate the PR spec's Share Experience section requires).
    // `formatVersion !== 1` covers both V2-V5 (`formatVersion: 2`) and
    // Daily V6 (`formatVersion: 3`, PR25) - a V1 date never has Daily
    // Points at all, the only format excluded here.
    const canShare = !isQaPreview && this.submitState === "done" && outcome.status === "completed" && spec.formatVersion !== 1 && isSharingEnabled();

    if (this.comparison && sourceChallenge) {
      y = this.renderComparisonBanner(centerX, y, width, this.comparison, sourceChallenge.creatorDisplayName);
    }

    if (canShare && this.shareCreateError) {
      const feedback = this.add
        .text(centerX, y, this.shareCreateError, {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          color: "#ff5f6d",
          align: "center",
          wordWrap: { width: Math.min(420, width - 40) },
        })
        .setOrigin(0.5, 0);
      this.mainContainer.add(feedback);
      y = feedback.y + feedback.height + 14;
    }

    // Most important actions first, side by side whenever there's room:
    // CHALLENGE A FRIEND (only when `canShare`) gets its own full-width
    // row above the rest, then TRY AGAIN (hidden once attempts are
    // exhausted - the row reflows to just DAILY LEADERBOARD centered,
    // never an empty slot) paired with DAILY LEADERBOARD, then MAIN MENU
    // centered in its own final row. `y` here is already the measured
    // bottom of whatever content was actually rendered above (attempts
    // remaining, the comparison banner, share feedback, or the "last
    // attempt" message, whichever apply) - a further responsive gap is
    // added on top so the first row never starts flush against that text.
    //
    // Instant Play & Onboarding V1: EXPLORE THE WORLDS is added as one
    // more full-width row - promoted ABOVE everything else (primary
    // style) only when neither a stronger existing next action remains
    // (no Daily attempts left AND no CHALLENGE A FRIEND opportunity),
    // otherwise appended AFTER MAIN MENU (secondary style). Never
    // replaces or reorders TRY AGAIN/CHALLENGE A FRIEND/DAILY LEADERBOARD/
    // MAIN MENU themselves.
    const promoteWorldsDiscovery = this.shouldShowWorldsDiscovery && attemptsRemaining <= 0 && !canShare;
    const appendWorldsDiscovery = this.shouldShowWorldsDiscovery && !promoteWorldsDiscovery;
    const gridWidth = Math.min(420, width - 40);
    const gridRowHeight = Math.max(56, Math.min(66, height * 0.08));
    const gridStartY = contentFlowStartY(y, height, { floor: 28, cap: 44, factor: 0.04 });
    const baseRows: (ButtonSlot | null)[][] = canShare
      ? [[{}], [attemptsRemaining > 0 ? {} : null, {}], [{}]]
      : [
          [attemptsRemaining > 0 ? {} : null, {}],
          [{}],
        ];
    const rows = promoteWorldsDiscovery ? [[{}], ...baseRows] : appendWorldsDiscovery ? [...baseRows, [{}]] : baseRows;
    const { placements } = layoutButtonRows(rows, { availableWidth: gridWidth, startY: gridStartY, rowHeight: gridRowHeight });
    let rowIndex = 0;
    const explorePromotedPlacement = promoteWorldsDiscovery ? placements[rowIndex++]![0] : null;
    const [sharePlacement] = canShare ? placements[rowIndex++] : [null];
    const [tryAgainPlacement, leaderboardPlacement] = placements[rowIndex++];
    const [menuPlacement] = placements[rowIndex++];
    const exploreAppendedPlacement = appendWorldsDiscovery ? placements[rowIndex++]![0] : null;
    const explorePlacement = explorePromotedPlacement ?? exploreAppendedPlacement;

    if (explorePlacement) {
      const exploreButton = createButton(this, centerX + explorePlacement.x, explorePlacement.y, "EXPLORE THE WORLDS", () => this.exploreTheWorlds(), {
        width: explorePlacement.width,
        fontSize: explorePlacement.isTwoColumn ? "15px" : "20px",
        ...(appendWorldsDiscovery ? { fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#f5f7fa" } : {}),
      });
      this.mainContainer.add(exploreButton);
    }

    if (sharePlacement) {
      const shareButton = createButton(
        this,
        centerX + sharePlacement.x,
        sharePlacement.y,
        this.isCreatingChallenge ? "…" : "CHALLENGE A FRIEND",
        () => void this.challengeAFriend(),
        { width: sharePlacement.width, fontSize: sharePlacement.isTwoColumn ? "15px" : "20px", disabled: this.isCreatingChallenge },
      );
      this.mainContainer.add(shareButton);
    }

    if (tryAgainPlacement) {
      const tryAgain = createButton(this, centerX + tryAgainPlacement.x, tryAgainPlacement.y, "TRY AGAIN", () => this.tryAgain(), {
        width: tryAgainPlacement.width,
        fontSize: tryAgainPlacement.isTwoColumn ? "15px" : "20px",
      });
      this.mainContainer.add(tryAgain);
    }

    const leaderboardButton = createButton(
      this,
      centerX + leaderboardPlacement!.x,
      leaderboardPlacement!.y,
      "DAILY LEADERBOARD",
      () => this.scene.start("DailyLeaderboardScene", { challengeId: this.resultData.spec.challengeId }),
      {
        width: leaderboardPlacement!.width,
        fontSize: leaderboardPlacement!.isTwoColumn ? "15px" : "18px",
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
      },
    );
    this.mainContainer.add(leaderboardButton);

    const menuButton = createButton(this, centerX + menuPlacement!.x, menuPlacement!.y, "MAIN MENU", () => this.scene.start("MenuScene"), {
      width: menuPlacement!.width,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(menuButton);
  }

  /** Renders the win/tie/loss headline/subline pair (`describeComparison`) against a challenge's creator - same wording `ScoreChallengeScene` uses for the exact same comparison, from the opposite side. */
  private renderComparisonBanner(centerX: number, y: number, width: number, comparison: ScoreComparisonResult, creatorDisplayName: string): number {
    const { headline, subline } = describeComparison(comparison, creatorDisplayName);
    const color = comparison.outcome === "beat" ? "#7dffb3" : comparison.outcome === "tie" ? "#ffd166" : "#ff5f6d";
    const wrapWidth = Math.min(420, width - 40);

    const headlineText = this.add
      .text(centerX, y, headline, { fontFamily: "Arial, sans-serif", fontSize: "18px", fontStyle: "bold", color, align: "center", wordWrap: { width: wrapWidth } })
      .setOrigin(0.5);
    this.mainContainer.add(headlineText);
    y = headlineText.y + headlineText.height + (subline ? 4 : 0);

    if (subline) {
      const sublineText = this.add.text(centerX, y, subline, { fontFamily: "Arial, sans-serif", fontSize: "13px", color }).setOrigin(0.5);
      this.mainContainer.add(sublineText);
      y += sublineText.height;
    }

    return y + 16;
  }

  /**
   * The QA-preview tail of `buildLayout()` - no submitting/offline/error
   * state, no rank, no real attempts-remaining/"last attempt" line, no
   * DAILY LEADERBOARD button: none of that applies to a run that was
   * never submitted or reserved. Just REPLAY QA PREVIEW (another
   * unlimited run of the same spec) and BACK TO QA PREVIEW (returns to
   * `DailyChallengeScene`, which re-reads the still-persisted QA date
   * override and lands back on the same "qa-preview" screen - see
   * `dailyQa.ts`'s `setDailyQaDate`).
   */
  private renderQaPreviewActions(centerX: number, y: number, width: number, height: number): void {
    const note = this.add
      .text(centerX, y, "This run wasn't saved, ranked, or counted against your real attempts.", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: Math.min(420, width - 40) },
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(note);
    y = note.y + note.height + 20;

    const gridWidth = Math.min(420, width - 40);
    const gridRowHeight = Math.max(56, Math.min(66, height * 0.08));
    const gridStartY = contentFlowStartY(y, height, { floor: 28, cap: 44, factor: 0.04 });
    const { placements } = layoutButtonRows([[{}, {}]], { availableWidth: gridWidth, startY: gridStartY, rowHeight: gridRowHeight });
    const [replayPlacement, backPlacement] = placements[0];

    const replay = createButton(this, centerX + replayPlacement!.x, replayPlacement!.y, "REPLAY QA PREVIEW", () => this.replayQaPreview(), {
      width: replayPlacement!.width,
      fontSize: replayPlacement!.isTwoColumn ? "15px" : "18px",
      fillColor: GameConfig.colors.star,
      hoverColor: 0xffd166,
      textColor: "#0f1117",
    });
    this.mainContainer.add(replay);

    const back = createButton(this, centerX + backPlacement!.x, backPlacement!.y, "BACK TO QA PREVIEW", () => this.tryAgain(), {
      width: backPlacement!.width,
      fontSize: backPlacement!.isTwoColumn ? "15px" : "18px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(back);
  }
}
